import { ID, Permissions } from "@nova/constants"
import { useTranslation } from "@nova/use-translation"
import {
    useMemberPermissions,
    useWorkspacePermissions,
} from "features/data-management/workspace/useWorkspacePermissions"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useState } from "react"
import { AccessRequestButtons } from "./AccessRequestButtons"
import { PermissionUpdates } from "./InviteEverywhereModalContainer"
import { WorkspaceCollaboratorProfile } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceCollaboratorProfile"
import { WorkspaceAccessSplitButton } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceAccessSplitButton"
import { WorkspaceMember } from "@nova/schemas"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"
import { DiscriminatedUnion } from "@nova/util-ts"

export const InviteEverywhereCollaborator = ({
    member,
    permissionUpdates,
    entityId,
    readOnly,
    setPermissionUpdates,
}: {
    member: DiscriminatedUnion<WorkspaceMember>
    permissionUpdates: PermissionUpdates
    entityId: ID
    readOnly?: boolean
    setPermissionUpdates: React.Dispatch<React.SetStateAction<PermissionUpdates>>
}) => {
    const { t } = useTranslation()
    const { isAdmin, selfId } = useWorkspacePermissions()
    const { permissions: existingPermissions } = useMemberPermissions(member.userId, entityId)

    const workspaceId = useAppSelector(selectActiveWorkspaceId())

    const defaultPermissions =
        permissionUpdates[member.userId]?.newPermission || existingPermissions
    const [dropdownSelection, setDropdownSelection] = useState(
        defaultPermissions === Permissions.None
            ? Permissions.None
            : defaultPermissions || member.permissions.workspaceAccess
    )

    useEffect(() => {
        setDropdownSelection(defaultPermissions)
    }, [defaultPermissions])

    const memberIsAdmin = member.permissions.workspaceAccess === Permissions.Admin
    const hasRequestedAccess = member.permissions.accessRequestIds?.includes(entityId)

    const handlePermissionSelection = useCallback(
        (selection: Permissions) => {
            setDropdownSelection(selection)

            setPermissionUpdates((prevUpdates) => {
                return {
                    ...prevUpdates,
                    [member.userId]: {
                        accessLevel: entityId !== workspaceId ? "subRole" : "workspace",
                        newPermission: selection,
                        entityId,
                    },
                }
            })
        },
        [member, entityId, workspaceId, setPermissionUpdates]
    )

    return (
        <div className="flex justify-between items-center">
            <UserProfileModalTrigger userId={member.userId}>
                <WorkspaceCollaboratorProfile
                    userId={member.userId}
                    email={member.email}
                    fullName={member.fullName}
                    trimEmail
                />
            </UserProfileModalTrigger>
            {!readOnly && !hasRequestedAccess && (
                <WorkspaceAccessSplitButton
                    defaultPermission={member.permissions.workspaceAccess || Permissions.None}
                    dropdownSelection={dropdownSelection || Permissions.None}
                    setDropdownSelection={handlePermissionSelection}
                    accessEntityId={entityId}
                    portalPositioning={{
                        portalId: "access-dropdown-portal",
                        relativeOffset: 40,
                    }}
                    disabled={selfId === member.userId || (!isAdmin && memberIsAdmin)}
                />
            )}
            {hasRequestedAccess && !readOnly && (
                <AccessRequestButtons entityId={entityId} member={member} />
            )}
            {readOnly && (
                <div className="flex justify-end mr-1.5 !text-nova-soft-white w-[122px] h-[38px] !text-left items-center">
                    {t(Permissions[dropdownSelection || Permissions.None])}
                </div>
            )}
        </div>
    )
}
